/**
 * Which of a Data frame's fields show as columns, and how wide. A table
 * keeps no "key" mark, so a key an agent writes rows by
 * ("2026-10-19|question|bing") is found by its values: a text field that is
 * other fields' values joined by one separator repeats them, and starts
 * hidden while other fields show. People show it again from Columns. A view
 * choice only: the table, and what agents read of it, never change.
 */
import { useState } from "react";
import type {
  DatasetFieldType,
  DatasetOption,
  DatasetValue,
} from "@/api/datasets";

interface ColumnField {
  id: string;
  type: DatasetFieldType;
  options?: readonly DatasetOption[];
}

interface ColumnRow {
  values: Readonly<Record<string, DatasetValue | undefined>>;
}

/** A join between parts: one to three marks, never letters or spaces only. */
const SEPARATOR = /^(?=.*[^\s\p{L}\p{N}])[^\p{L}\p{N}]{1,3}$/u;

/** A value as its text, a select's option id as its label (a card holds labels). */
function plain(field: ColumnField, value: DatasetValue | undefined): string {
  if (value === null || value === undefined) return "";
  if (field.type === "select" && typeof value === "string") {
    return field.options?.find((option) => option.id === value)?.label ?? value;
  }
  return String(value);
}

/** Whether every piece is one of `parts`, each part used at most once. */
function piecesOf(pieces: string[], parts: string[]): boolean {
  const left = new Map<string, number>();
  for (const part of parts) left.set(part, (left.get(part) ?? 0) + 1);
  return pieces.every((piece) => {
    const count = left.get(piece) ?? 0;
    left.set(piece, count - 1);
    return count > 0;
  });
}

/**
 * Whether `value` is at least two of `parts` joined by one separator. The
 * separator follows a part the value starts with; splitting on it keeps the
 * check linear in the value, whatever agents write into the rows.
 */
function composed(value: string, parts: string[]): boolean {
  const tried = new Set<string>();
  for (const first of parts) {
    if (!value.startsWith(first)) continue;
    for (let length = 1; length <= 3; length++) {
      const separator = value.slice(first.length, first.length + length);
      if (tried.has(separator) || !SEPARATOR.test(separator)) continue;
      tried.add(separator);
      const pieces = value.split(separator);
      if (pieces.length >= 2 && piecesOf(pieces, parts)) return true;
    }
  }
  return false;
}

/** The text fields every filled value of which joins other fields' values. */
export function derivedFieldIds(
  fields: readonly ColumnField[],
  rows: readonly ColumnRow[],
): Set<string> {
  const derived = new Set<string>();
  for (const field of fields) {
    if (field.type !== "text") continue;
    let filled = 0;
    const every = rows.every((row) => {
      const value = plain(field, row.values[field.id]);
      if (!value) return true;
      filled += 1;
      const parts = fields
        .filter((other) => other.id !== field.id)
        .map((other) => plain(other, row.values[other.id]))
        .filter((part) => part && part !== value);
      return composed(value, parts);
    });
    if (every && filled > 0) derived.add(field.id);
  }
  return derived.size < fields.length ? derived : new Set();
}

/** The narrowest column of each type; every column grows to its content. */
const MIN_WIDTH: Record<DatasetFieldType, string> = {
  text: "8rem",
  number: "4.5rem",
  decimal: "4.5rem",
  boolean: "4.5rem",
  date: "6rem",
  datetime: "6rem",
  select: "6rem",
};

/** A field's grid track: at least its type's width, else its content's. */
export function columnTrack(type: DatasetFieldType): string {
  return `minmax(${MIN_WIDTH[type]}, max-content)`;
}

/**
 * The widest a cell grows before its text ends in an ellipsis (the full
 * value shows on hover): a sentence of text reads whole, a label stays short.
 */
export function columnCap(type: DatasetFieldType): string {
  return type === "text" ? "max-w-[26rem]" : "max-w-[14rem]";
}

const NONE: ReadonlySet<string> = new Set();

/**
 * The fields shown as columns, in the table's order: a derived key starts
 * hidden, and Columns shows or hides any field but the last one shown. The
 * start is read once, from the first rows, so a column never comes or goes
 * as rows change or pages turn. A delete that leaves only hidden fields
 * shows them all.
 */
export function useDatasetColumns<Field extends ColumnField>(
  fields: readonly Field[],
  rows: readonly ColumnRow[],
) {
  const [chosen, setChosen] = useState<Readonly<Record<string, boolean>>>({});
  const [derived, setDerived] = useState<ReadonlySet<string> | null>(null);
  if (derived === null && rows.length > 0) {
    setDerived(derivedFieldIds(fields, rows));
  }
  const hidden = derived ?? NONE;
  const chosenShown = (id: string) => chosen[id] ?? !hidden.has(id);
  const anyShown = fields.some((field) => chosenShown(field.id));
  const isShown = (id: string) => !anyShown || chosenShown(id);
  const shown = fields.filter((field) => isShown(field.id));
  return {
    shown,
    isShown,
    /** Hiding the last shown column would leave no table. */
    canToggle: (id: string) => !isShown(id) || shown.length > 1,
    toggle: (id: string) =>
      setChosen((previous) => ({
        ...previous,
        // Out of the all-hidden fallback, the rest stay as they show now.
        ...(anyShown
          ? {}
          : Object.fromEntries(fields.map((field) => [field.id, true]))),
        [id]: !isShown(id),
      })),
  };
}
